/**
 * Phase 3 — Buyer Claim History + Redeem Card.
 * Endpoints:
 *   GET  /api/buyer/claims                → my redemptions
 *   GET  /api/claim/{token}               → peek before redeem
 *   POST /api/claim/{token}/redeem        → redeem
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift as GiftIcon, ArrowRight, CheckCircle2 } from 'lucide-react';
import theme from '../theme';
import { ClaimLifecycle } from '../components/ClaimLifecycle';
import { useNavigate } from 'react-router-dom';
import BenefitList from '../components/holder/BenefitList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};
const inputStyle = {
  width: '100%', padding: '10px 12px', border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

function extractToken(input) {
  if (!input) return '';
  const s = input.trim();
  try {
    const u = new URL(s);
    const m = u.pathname.match(/\/claim\/([^/?#]+)/);
    if (m) return m[1];
  } catch (_) { /* not a URL */ }
  const m2 = s.match(/\/claim\/([^/?#]+)/);
  if (m2) return m2[1];
  return s;
}

export default function BuyerClaimsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [peek, setPeek] = useState(null);
  const [peekErr, setPeekErr] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const navigate = useNavigate();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/buyer/claims`, auth);
      setRows(res.data?.claims || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load claims');
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const doPeek = async () => {
    setPeek(null); setPeekErr(null);
    const tok = extractToken(input);
    if (!tok) { toast.error('Paste a claim link or token'); return; }
    try {
      const res = await axios.get(`${API}/claim/${tok}`, auth);
      setPeek({ token: tok, ...(res.data?.claim || {}) });
    } catch (e) {
      setPeekErr(e?.response?.data?.detail || 'Claim not found');
    }
  };

  const doRedeem = async () => {
    if (!peek?.token) return;
    setRedeeming(true);
    try {
      const res = await axios.post(`${API}/claim/${peek.token}/redeem`, {}, auth);
      if (res.data?.duplicate) toast.info('Already claimed');
      else toast.success('Claim recorded');
      setPeek(null); setInput('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Redeem failed');
    } finally { setRedeeming(false); }
  };

  return (
    <div data-testid="buyer-claims-page" style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <GiftIcon size={22} color={theme.colors.primary} />
        <div style={{ fontSize: 22, fontWeight: 900, color: theme.colors.text.primary }}>My Claims</div>
      </div>
      <div style={{ color: theme.colors.text.muted, marginBottom: 20, fontSize: 13 }}>
        Redeem a claim link or QR you received, and see everything you've already claimed.
      </div>

      {/* Redeem Card */}
      <div data-testid="claim-redeem-card" style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: theme.colors.text.primary, marginBottom: 10 }}>
          Redeem a Claim Link
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input data-testid="claim-input" style={{ ...inputStyle, flex: 1, minWidth: 260 }}
            value={input} onChange={e => setInput(e.target.value)}
            placeholder="Paste claim URL or token" />
          <button data-testid="claim-peek-btn" onClick={doPeek}
            style={{ padding: '10px 14px', borderRadius: theme.radius.sm, border: 'none',
              background: theme.colors.primary, color: '#fff', cursor: 'pointer', fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            Preview <ArrowRight size={14} />
          </button>
        </div>

        {peekErr && <div data-testid="claim-peek-error" style={{ marginTop: 10, color: theme.colors.status.error, fontSize: 13 }}>{peekErr}</div>}

        {peek && (
          <div data-testid="claim-peek-block" style={{ marginTop: 12, padding: 14,
            background: theme.colors.bg.secondary, borderRadius: theme.radius.sm }}>
            <div style={{ fontWeight: 800, color: theme.colors.text.primary }}>{peek.title}</div>
            <div style={{ fontSize: 12, color: theme.colors.text.muted, margin: '4px 0 8px' }}>
              {peek.creator_name} · {peek.portfolio_name} · {peek.claim_type} · {peek.status}
            </div>
            {peek.description && <div style={{ fontSize: 13, color: theme.colors.text.secondary, marginBottom: 8 }}>{peek.description}</div>}
            <div style={{ fontSize: 11, color: '#7C2D12', marginBottom: 10 }}>Benefits vary by Kickback. Not every claim includes payouts.</div>
            {!peek.available && (
              <div data-testid="claim-peek-unavailable" style={{ fontSize: 12, color: theme.colors.status.error, marginBottom: 8 }}>
                This claim link is {peek.status} and can't be claimed. Your existing claims are listed below.
              </div>
            )}
            <button data-testid="claim-confirm-redeem-btn" disabled={redeeming || !peek.available} onClick={doRedeem}
              style={{ padding: '10px 16px', borderRadius: theme.radius.sm, border: 'none',
                background: peek.available ? theme.colors.status.success : theme.colors.text.muted,
                color: '#fff', cursor: peek.available ? 'pointer' : 'not-allowed', fontWeight: 700 }}>
              {redeeming ? 'Redeeming…' : peek.available ? 'Claim / Redeem' : 'Unavailable'}
            </button>
          </div>
        )}
      </div>

      {/* Claim History */}
      <div data-testid="claim-history-card" style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: theme.colors.text.primary }}>Claim History</div>
          <div style={{ fontSize: 12, color: theme.colors.text.muted }} data-testid="claim-history-count">
            {rows.length} claim{rows.length === 1 ? '' : 's'}
          </div>
        </div>

        {loading ? (
          <div data-testid="claim-history-loading" style={{ padding: 20, color: theme.colors.text.muted }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div data-testid="claim-history-empty" style={{ padding: 24, textAlign: 'center', color: theme.colors.text.muted }}>
            You haven't claimed any Kickbacks yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {rows.map(r => (
              <div key={r.id} data-testid={`claim-history-row-${r.id}`} role="button" tabIndex={0}
                onClick={() => navigate(`/buyer/claims/${r.id}`)}
                onKeyDown={e => { if (e.key === 'Enter') navigate(`/buyer/claims/${r.id}`); }}
                style={{ display: 'flex', gap: 12, padding: 12, background: theme.colors.bg.secondary, cursor: 'pointer',
                  borderRadius: theme.radius.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                <CheckCircle2 size={18} color={theme.colors.status.success} />
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 700, color: theme.colors.text.primary }}>
                    {r.title || r.portfolio_name || 'Kickback'}
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.text.muted }}>
                    {r.portfolio_name && <>{r.portfolio_name} · </>}
                    {new Date(r.redeemed_at).toLocaleString()}
                  </div>
                </div>
                <span data-testid={`claim-history-issuance-${r.id}`} style={{ padding: '2px 8px', borderRadius: 999,
                  background: r.issuance?.status === 'issued' ? `${theme.colors.status.success}1A` : '#FEF3C7',
                  color: r.issuance?.status === 'issued' ? theme.colors.status.success : '#92400E',
                  fontWeight: 800, fontSize: 10, textTransform: 'uppercase' }}>
                  NFT: {r.issuance?.label || 'Not available yet'}
                </span>
                <div style={{ flexBasis: '100%', paddingLeft: 30, display: 'grid', gap: 10 }}>
                  <ClaimLifecycle claimedAt={r.redeemed_at} entitlementStatus={r.entitlement?.status}
                    issuance={r.issuance} testidPrefix={`claim-history-${r.id}`} />
                  <div data-testid={`claim-history-benefits-${r.id}`} style={{ fontSize: 12 }}>
                    <div style={{ fontWeight: 700, color: theme.colors.text.primary, marginBottom: 4 }}>Benefits</div>
                    <div onClick={e => e.stopPropagation()}><BenefitList benefits={r.eligible_benefits} portfolioId={r.portfolio_id} compact /></div>
                    <div data-testid={`claim-history-open-${r.id}`} style={{ color: theme.colors.primary, marginTop: 6, fontSize: 12, fontWeight: 700 }}>
                      View claim details and redeem →
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
